/**
 * OTRACK-SUBSYSTEM — module-level "is Shift currently held" tracker.
 *
 * POLARMODE bit 8 ("press SHIFT to acquire object-snap tracking points")
 * gates hover-dwell acquisition on a held Shift key. The dwell timer lives
 * in the NON-REACT pick-loop closure (`interactivePickLoop.ts`), which needs
 * a SYNCHRONOUS read of "is Shift down right now" — a plain module-level
 * boolean updated by its own keydown/keyup listener, mirroring
 * `sysvarHotkeys.ts`'s own `heldOverrideKeys` module-level pattern.
 * Deliberately INDEPENDENT of `sysvarHotkeys.ts`'s own Shift handling — the
 * DOM event model supports multiple independent listeners for the same key
 * with zero conflict, so this never competes with (or needs to touch) that
 * module's temp-override logic.
 *
 * Deliberately UNCONDITIONAL — no `isEditableTarget` guard (local Codex
 * review round 3 P2 — "Track Shift while command inputs have focus"): while
 * drafting, focus very commonly sits in the command-line / dynamic-input
 * text field (right after typing a command name or a coordinate), and this
 * tracker only OBSERVES physical key state for a later async gate check —
 * it never consumes the keystroke or drives a discrete action the way
 * `sysvarHotkeys.ts`'s F-key toggles do, so there is nothing to protect a
 * text field FROM. Guarding here just meant Shift-to-acquire silently never
 * worked whenever focus happened to be in an editable field.
 *
 * Local Codex review round 9 P2 ("Refresh snap when Shift-to-acquire is
 * pressed") — `installOtrackShiftTracker` takes an optional `onShiftChange`
 * callback, invoked on every GENUINE Shift press/release transition (never
 * on OS key-repeat, which re-fires `keydown` while already held). Without
 * this, POLARMODE bit 8 + a cursor already resting on an OSNAP candidate
 * meant pressing Shift only flipped this module's own `shiftHeld` flag —
 * nothing re-ran `resolveAndCacheSnap` (the pick loop only re-evaluates on
 * a real pointer move or an explicit refresh trigger), so the documented
 * "press SHIFT to acquire" path silently did nothing until the user ALSO
 * moved the mouse. `useOtrackAcquisitionEffects` wires this to
 * `refreshSnapAtLastPointerRef`, the SAME re-resolve every other OTRACK
 * stale-cache trigger in this codebase already uses.
 */

let shiftHeld = false;
let installed = false;
let onShiftChangeCb: (() => void) | null = null;

function handleKeyDown(e: KeyboardEvent): void {
  if (e.key !== "Shift" || shiftHeld) return; // ignore OS key-repeat
  shiftHeld = true;
  onShiftChangeCb?.();
}

function handleKeyUp(e: KeyboardEvent): void {
  if (e.key !== "Shift" || !shiftHeld) return;
  shiftHeld = false;
  onShiftChangeCb?.();
}

/**
 * Mount once (from `useOtrackLifecycleEffects`); idempotent. Returns the
 * teardown. `onShiftChange` (round 9 P2) is invoked synchronously on every
 * genuine press/release transition — pass a STABLE wrapper (e.g.
 * `() => someRef.current?.()`) so it always reads the caller's latest live
 * value, not a snapshot captured at install time.
 */
export function installOtrackShiftTracker(onShiftChange?: () => void): () => void {
  onShiftChangeCb = onShiftChange ?? null;
  if (!installed && typeof window !== "undefined") {
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    installed = true;
  }
  return () => {
    if (installed) {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      installed = false;
    }
    shiftHeld = false;
    onShiftChangeCb = null;
  };
}

/** Synchronous read for the non-React pick-loop dwell timer. */
export function isOtrackShiftHeld(): boolean {
  return shiftHeld;
}

/** Test-only: reset between cases. */
export function __resetOtrackShiftTrackerForTest(): void {
  shiftHeld = false;
  installed = false;
  onShiftChangeCb = null;
}
